import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Types for recording state
interface RecordingConfig {
  uploadInterval: number; // in seconds
  maxRetries: number;
  bufferSize: number; // in samples
  silenceThreshold: number;
  silenceDurationThreshold: number; // in milliseconds
}

interface RecordingState {
  isRecording: boolean;
  isPaused: boolean;
  isStopped: boolean;
  currentPartNumber: number;
  lastPartNumber: number;
  appointmentId: string | null;
  patientId: string | null;
  locationId: string | null;
  recordingStartTime: number | null;
  config: RecordingConfig;
  error: string | null;
  isJobCompleted: boolean;
  pollingActive: boolean;
  processingStarted: boolean; // becomes true after stopRecordingAndStartProcessing response
}

const initialState: RecordingState = {
  isRecording: false,
  isPaused: false,
  isStopped: false,
  currentPartNumber: 1, // Start from 1 as per requirements
  lastPartNumber: 0, // Track the last completed part number
  appointmentId: null,
  patientId: null,
  locationId: null,
  recordingStartTime: null,
  config: {
    uploadInterval: 30, // 30 seconds as per requirements
    maxRetries: 3,
    bufferSize: 4096,
    silenceThreshold: 0.01,
    silenceDurationThreshold: 200, // 200ms silence as per requirements (600ms is good for medical transcription)
  },
  error: null,
  isJobCompleted: false,
  pollingActive: false,
  processingStarted: false,
};

const recordingSlice = createSlice({
  name: 'recording',
  initialState,
  reducers: {
    // Recording control actions
    startRecording: (
      state,
      action: PayloadAction<{
        appointmentId: string;
        patientId: string;
        locationId: string;
        partNumber?: number;
      }>
    ) => {
      state.isRecording = true;
      state.isPaused = false;
      state.isStopped = false;
      state.appointmentId = action.payload.appointmentId;
      state.patientId = action.payload.patientId;
      state.locationId = action.payload.locationId;
      state.currentPartNumber = action.payload.partNumber || 1; // Start from 1
      state.lastPartNumber = 0; // Reset last part number for new recording
      state.recordingStartTime = Date.now();
      state.error = null;
      state.processingStarted = false; // reset processing flag for new session
    },

    pauseRecording: (state) => {
      state.isPaused = true;
    },

    resumeRecording: (state) => {
      state.isPaused = false;
    },

    stopRecording: (state) => {
      state.isRecording = false;
      state.isPaused = false;
      state.isStopped = true;
      state.error = null;
      state.processingStarted = true;
    },

    resetRecording: (state) => {
      state.isRecording = false;
      state.isPaused = false;
      state.isStopped = false;
      state.currentPartNumber = 1; // Reset to 1 for next recording
      state.lastPartNumber = 0;
      state.appointmentId = null;
      state.patientId = null;
      state.locationId = null;
      state.recordingStartTime = null;
      state.error = null;
      state.isJobCompleted = false;
      state.pollingActive = false;
      state.processingStarted = false;
    },
    // Mark that stopRecordingAndStartProcessing has been acknowledged
    setProcessingStarted: (state, action: PayloadAction<boolean>) => {
      state.processingStarted = action.payload;
    },

    // Part number management
    incrementPartNumber: (state) => {
      state.lastPartNumber = state.currentPartNumber; // Mark current as completed
      state.currentPartNumber++; // Increment for next part
    },

    updateLastPartNumber: (
      state,
      action: PayloadAction<{
        patientId: string;
        appointmentId: string;
        lastPartNumber: number;
      }>
    ) => {
      if (
        state.patientId === action.payload.patientId &&
        state.appointmentId === action.payload.appointmentId
      ) {
        state.lastPartNumber = action.payload.lastPartNumber;
        state.currentPartNumber = action.payload.lastPartNumber;
      }
    },

    // Configuration actions
    updateRecordingConfig: (
      state,
      action: PayloadAction<Partial<RecordingConfig>>
    ) => {
      state.config = { ...state.config, ...action.payload };
    },

    // Error handling
    setRecordingError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },

    clearRecordingError: (state) => {
      state.error = null;
    },

    // Job completion status
    setJobCompletionStatus: (
      state,
      action: PayloadAction<{
        patientId: string;
        appointmentId: string;
        isJobCompleted: boolean;
      }>
    ) => {
      if (
        state.patientId === action.payload.patientId &&
        state.appointmentId === action.payload.appointmentId
      ) {
        state.isJobCompleted = action.payload.isJobCompleted;
      }
    },

    // Polling control
    startPolling: (state) => {
      state.pollingActive = true;
    },

    stopPolling: (state) => {
      state.pollingActive = false;
    },
  },
});

export const {
  startRecording,
  pauseRecording,
  resumeRecording,
  stopRecording,
  resetRecording,
  incrementPartNumber,
  updateLastPartNumber,
  updateRecordingConfig,
  setRecordingError,
  clearRecordingError,
  setJobCompletionStatus,
  startPolling,
  stopPolling,
  setProcessingStarted,
} = recordingSlice.actions;

export default recordingSlice.reducer;
